'use client';

import Image from 'next/image';
import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { TypeSocialProofFields } from '@/core/generated-types';
import { VideoItem } from './components/VideoItem';
import { SocialItem } from './components/SocialItem';
import { EditorialItem } from './components/EditorialItem';
import { BREAKPOINTS } from '@/core/constants';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

export const Card = ({
  children,
  classes,
}: {
  children: React.ReactNode;
  classes?: string;
}) => {
  return (
    <div
      className={classNames(
        'rounded-lg shadow-2xl mb-4 lg:mb-8 bg-white',
        classes,
      )}
    >
      {children}
    </div>
  );
};

const SocialProof: FC<TypeSocialProofFields> = ({
  title,
  subtitle,
  reviews,
  starsAsset,
  desktopItems,
  mobileItems,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.md);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section
      data-testid={`social-proof`}
      className="bg-blue-tint pt-8 pb-12 lg:pt-20 lg:pb-16"
    >
      <div className="flex flex-col gap-4 px-4">
        <Heading
          tag={HeadingTag.H2}
          size={HeadingSize.H2}
          classes="leading-10"
          align="center"
          text={title}
          hoverEffect
        />
        <Heading
          tag={HeadingTag.H3}
          size={HeadingSize.H3}
          classes="leading-8"
          align="center"
          text={subtitle}
          hoverEffect
        />
        <div className="flex gap-2 justify-center items-center">
          {starsAsset && (
            <Image
              src={`https:${starsAsset.fields.file?.url}`}
              width={150}
              height={25}
              alt="Lovevery"
            />
          )}
          <p className="text-[18px] md:text-xl text-indigo">{reviews}</p>
        </div>
      </div>
      <div className="mx-auto mt-8 px-2 max-w-[1400px] ">
        {isMobile ? (
          <div className="gap-4 lg:gap-8 columns-2 md:columns-4">
            {mobileItems?.map((item, index) => {
              switch (item.sys.contentType.sys.id.toLowerCase()) {
                case 'socialproofavataritem':
                  return <SocialItem key={index} {...item.fields} />;
                case 'socialproofeditorialitem':
                  return <EditorialItem key={index} {...item.fields} />;
                case 'socialproofvideoitem':
                  return <VideoItem key={index} {...item.fields} />;
                default:
                  return null;
              }
            })}
          </div>
        ) : (
          <div className="gap-4 lg:gap-8 columns-2 md:columns-4">
            {desktopItems?.map((item, index) => {
              switch (item.sys.contentType.sys.id.toLowerCase()) {
                case 'socialproofavataritem':
                  return <SocialItem key={index} {...item.fields} />;
                case 'socialproofeditorialitem':
                  return <EditorialItem key={index} {...item.fields} />;
                case 'socialproofvideoitem':
                  return <VideoItem key={index} {...item.fields} />;
                default:
                  return null;
              }
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialProof;
